import { useQuery } from '@tanstack/react-query';
import { Encounter } from 'src/models';
import { ambientApi, api } from 'src/services';
import { UseQueryOverrideOptions } from '../common';

export type UseGetEncounterDetailsParams = {
  encounterId: string;
};

export type GetEncounterDetailsResponse = Encounter;

export const useGetEncounterDetails = (
  { encounterId }: UseGetEncounterDetailsParams,
  options: UseQueryOverrideOptions<GetEncounterDetailsResponse> = {}
) => {
  return useQuery({
    ...options,
    queryKey: ['encounterDetails', encounterId],
    queryFn: () =>
      ambientApi
        .get<GetEncounterDetailsResponse>(`/encounters/${encounterId}`)
        .then((res) => res.data),
  });
};

export type UseGetEncounterChatHistoryParams = {
  encounterId: string;
};

export type ToolCall = {
  type: 'function';
  name: string;
  id: string;
  args: Record<string, unknown>;
};

/**
 * Describes the kind of data in ToolResponse.content:
 * - text: Plain string (human-readable message, error, or raw text).
 * - json: JSON-serialized data; content is a string of valid JSON (e.g. API response, structured payload).
 * - patient_document_id: A document ID string pointing to a patient document. mime_type is already present with the PatientDocument.
 */
export type ToolResponse = Pick<ToolCall, 'type' | 'name' | 'id'> & {
  content: string;
  content_type: 'text' | 'json' | 'patient_document_id';
  mime_type?: string;
};

export type UserChatMessage = {
  classification?: null;
  content: string;
  flagged?: boolean;
  phase?: 'chat';
  role: 'assistant' | 'user';
  timestamp: string;
  trace_id?: string;
  tool_calls?: ToolCall[];
};

export type ToolResponseChatMessage = ToolResponse & { role: 'tool_response' };

export type ChatMessage = UserChatMessage | ToolResponseChatMessage;

export type GetEncounterChatHistoryResponse = {
  chat_message: ChatMessage[];
  progress: number;
};

export const useGetEncounterChatHistory = ({
  encounterId,
}: UseGetEncounterChatHistoryParams) => {
  return useQuery({
    queryKey: ['encounter', encounterId, 'chatHistory'],
    queryFn: () =>
      api
        .get<GetEncounterChatHistoryResponse>(
          `/v3/encounter/${encounterId}/chat_history`
        )
        .then(({ data }) => {
          const { chat_message = [], progress = 0 } = data || {};
          return {
            chatMessage: chat_message,
            progress,
          };
        }),
  });
};
